import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {selectPrices} from '../selectors';
import {updateSubscription} from '../slices/accountSlice';
import agent from '../agent/agent';

export const fetchPlans = createAsyncThunk('plan/fetchAll', (data, {getState}) => (
	agent
		.get(`${getState().status.apiUrl}/plan/available`)
		.set('Accept', 'application/json')
		.then((response) => response.body)
		.catch((error) => error))
);

export const logSubscriptionEvent = createAsyncThunk(
	'plan/logSubscriptionEvent',
	async (data, {getState, dispatch}) => {
		const response = await agent
			.post(`${getState().status.apiUrl}/payment/events`)
			.send(data)
			.set('Accept', 'application/json');
		return response.body;
	}
);

// update subscription – plan/price/status etc – state based on server response
const handleSubscriptionResponse = (response, state, dispatch, additionalValues = {}) => {
	const newSub = response.body.item;
	const price = selectPrices(state).find((p) => p.id === newSub.priceId);
	const plan = state.plan.plans.find((pl) => pl.prices.find((p) => p.id === newSub.priceId));
	dispatch(
		updateSubscription({
			price: price,
			plan: plan,
			status: newSub.status,
			...additionalValues,
		})
	);
	return response.body;
};

export const subscribeWithSEPA = createAsyncThunk(
	'subscription/createWithSEPA',
	async (data, {getState, dispatch}) => {
		const state = getState();
		const response = await agent
			.post(`${getState().status.apiUrl}/subscription/createWithSEPA`)
			.send(data)
			.set('Accept', 'application/json')
			.catch((error) => error);
		return handleSubscriptionResponse(response, state, dispatch);
	}
);

export const changeSubscription = createAsyncThunk(
	'subscription/change',
	async (data, {getState, dispatch}) => {
		const state = getState();
		const {csrf, sid} = state.account.data.api;
		const response = await agent
			.post(`${state.status.apiUrl}/subscription/change`)
			.send(data)
			.set('Authorization', `Bearer ${sid}`)
			.set('X-CSRF-Token', csrf)
			.set('Accept', 'application/json');
		return handleSubscriptionResponse(response, state, dispatch);
	}
);

export const cancelSubscription = createAsyncThunk(
	'subscription/cancel',
	async (data, {getState, dispatch}) => {
		const state = getState();
		const {csrf, sid} = state.account.data.api;
		const response = await agent
			.post(`${state.status.apiUrl}/subscription/cancel`)
			.send(data)
			.set('Authorization', `Bearer ${sid}`)
			.set('X-CSRF-Token', csrf)
			.set('Accept', 'application/json');
		return handleSubscriptionResponse(response, state, dispatch, {
			scheduledToCancelAt: response.body.item.scheduledToCancelAt
		});
	}
);

export const uncancelSubscription = createAsyncThunk(
	'subscription/uncancel',
	async (data, {getState, dispatch}) => {
		const state = getState();
		const {csrf, sid} = state.account.data.api;
		const response = await agent
			.post(`${state.status.apiUrl}/subscription/uncancel`)
			.send(data)
			.set('Authorization', `Bearer ${sid}`)
			.set('X-CSRF-Token', csrf)
			.set('Accept', 'application/json');
		return handleSubscriptionResponse(response, state, dispatch, {
			scheduledToCancelAt: ''
		});
	}
);



export const getCustomerDashboardLink = createAsyncThunk(
	'plan/getCustomerDashboardLink',
	async (eventData, {getState, dispatch}) => {
		const {csrf, sid} = getState().account.data.api;
		const response = await agent
			.get(`${getState().status.apiUrl}/payment/dashboard`)
			.send(eventData)
			.set('Authorization', `Bearer ${sid}`)
			.set('X-CSRF-Token', csrf)
			.set('Accept', 'application/json');
		return response.body;
	}
);

export const planSlice = createSlice({
	name: 'plan',
	initialState: {
		loading: '',
		error: null,
		dashboardLink: null,
		plans: [],
	},
	reducers: {},
	extraReducers: (builder) => {
		builder.addCase(fetchPlans.pending, (state, action) => {
			state.loading = 'plans';
		});
		builder.addCase(fetchPlans.fulfilled, (state, action) => {
			state.plans = action.payload.items;
			state.loading = '';
		});
		builder.addCase(fetchPlans.rejected, (state, action) => {
			state.error = action.error.message;
			state.loading = '';
		});
		builder.addCase(getCustomerDashboardLink.pending, (state, action) => {
			state.loading = 'dashboardLink';
		});
		builder.addCase(getCustomerDashboardLink.fulfilled, (state, action) => {
			state.dashboardLink = action.payload.general;
			state.loading = '';
		});
		builder.addCase(getCustomerDashboardLink.rejected, (state, action) => {
			state.error = action.error.message;
			state.loading = '';
		});
		builder.addCase(changeSubscription.pending, (state, action) => {
			state.loading = 'changeSubscription';
		});
		builder.addCase(changeSubscription.fulfilled, (state, action) => {
			state.loading = '';
		});
		builder.addCase(changeSubscription.rejected, (state, action) => {
			state.error = action.error.message;
			console.log(state.error);
			state.loading = '';
		});
		builder.addCase(cancelSubscription.pending, (state, action) => {
			state.loading = 'cancelSubscription';
		});
		builder.addCase(cancelSubscription.fulfilled, (state, action) => {
			state.loading = '';
		});
		builder.addCase(cancelSubscription.rejected, (state, action) => {
			state.error = action.error.message;
			console.log(state.error);
			state.loading = '';
		});
		builder.addCase(subscribeWithSEPA.pending, (state, action) => {
			state.loading = 'subscribeWithSEPA';
		});
		builder.addCase(subscribeWithSEPA.fulfilled, (state, action) => {
			state.loading = '';
		});
		builder.addCase(subscribeWithSEPA.rejected, (state, action) => {
			state.error = action.error.message;
			console.log(state.error);
			state.loading = '';
		});
	},
});

export default planSlice.reducer;
